'use client';

import { Box, CssBaseline } from '@mui/material';
import { AppRouterCacheProvider } from '@mui/material-nextjs/v14-appRouter';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { usePathname } from 'next/navigation';
import { ErrorBoundary } from 'react-error-boundary';
import * as Sentry from '@sentry/nextjs';
import { Bounce, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { MaintenanceBanner } from '@ui';
import CustomThemeProvider from '../components/CustomThemeProvider';
import Fallback from '../components/Fallback';
import { getBrokerFromHostName } from '../services/broker';
import queryClient from '../store/queryClient';
import './global.css';

const localStore =
  typeof window !== 'undefined' ? window.localStorage : undefined;

const persister = createSyncStoragePersister({
  storage: localStore,
});

if (localStore) {
  localStore.setItem('intraday-build', `${process.env.NODE_ENV}`);
}

export default function RootLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const NO_PADDING_ROUTES = ['/get-started'];
  const pathName = usePathname();
  const hasNoPadding = NO_PADDING_ROUTES.includes(pathName);

  return (
    <html lang="en">
      <body>
        <PersistQueryClientProvider
          client={queryClient}
          persistOptions={{
            persister,
            dehydrateOptions: {
              shouldDehydrateQuery: (e) => {
                return (
                  e.state.status === 'success' && e?.meta?.persist === true
                );
              },
            },
          }}
        >
          <AppRouterCacheProvider>
            <ErrorBoundary
              FallbackComponent={Fallback}
              onError={(error, errorInfo) => {
                Sentry.captureException(error, {
                  tags: {
                    component: 'error-boundary',
                    error_type: 'react_error_boundary',
                    crash: 'true',
                    environment: process.env.NEXT_PUBLIC_ENV,
                  },
                  extra: {
                    errorInfo: errorInfo,
                    componentStack: errorInfo?.componentStack,
                    pathname: window?.location?.pathname,
                    timestamp: new Date().toISOString(),
                  },
                  level: 'error',
                });
              }}
            >
              <CustomThemeProvider>
                <CssBaseline />
                <MaintenanceBanner
                  product="intraday"
                  broker={getBrokerFromHostName()}
                />
                <Box {...(hasNoPadding ? {} : { px: 2, py: 4 })}>{children}</Box>
              </CustomThemeProvider>
            </ErrorBoundary>
          </AppRouterCacheProvider>
        </PersistQueryClientProvider>
        <ToastContainer
          position="bottom-center"
          autoClose={5000}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
          transition={Bounce}
          hideProgressBar={true}
          style={{ marginBottom: '12px' }}
        />
      </body>
    </html>
  );
}
